// useWorkTimer.js - Hook for work discipline timer management
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'fixarr-work-sessions';
const ACTIVE_SESSION_KEY = 'fixarr-active-session';

export const useWorkTimer = () => {
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [sessionTime, setSessionTime] = useState(0);
    const [workSessions, setWorkSessions] = useState({});

    // Load saved sessions from localStorage
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                setWorkSessions(JSON.parse(saved));
            } catch (e) {
                console.error('Error loading work sessions:', e);
            }
        }

        // Check if there's an active session
        const activeSession = localStorage.getItem(ACTIVE_SESSION_KEY);
        if (activeSession) {
            try {
                const session = JSON.parse(activeSession);
                setActiveTaskId(session.taskId);
                setIsPaused(session.isPaused || false);
                // Calculate elapsed time since session start
                if (!session.isPaused) {
                    const elapsed = Math.floor((Date.now() - session.lastResumeTime) / 1000);
                    setSessionTime(session.accumulatedTime + elapsed);
                } else {
                    setSessionTime(session.accumulatedTime);
                }
            } catch (e) {
                localStorage.removeItem(ACTIVE_SESSION_KEY);
            }
        }
    }, []);

    // Timer tick
    useEffect(() => {
        let interval;
        if (activeTaskId && !isPaused) {
            interval = setInterval(() => {
                setSessionTime(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [activeTaskId, isPaused]);

    // Save work sessions
    const saveWorkSessions = useCallback((sessions) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
        setWorkSessions(sessions);
    }, []);

    // Start or resume timer for a task
    const startTimer = useCallback((taskId) => {
        // If starting a different task, stop the current one first
        if (activeTaskId && activeTaskId !== taskId) {
            stopTimer();
        }

        setActiveTaskId(taskId);
        setIsPaused(false);
        if (activeTaskId !== taskId) {
            setSessionTime(0);
        }

        // Save active session
        localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
            taskId,
            lastResumeTime: Date.now(),
            accumulatedTime: activeTaskId === taskId ? sessionTime : 0,
            isPaused: false
        }));
    }, [activeTaskId, sessionTime]);

    // Pause timer (manual rest)
    const pauseTimer = useCallback(() => {
        setIsPaused(true);

        // Update active session
        const current = localStorage.getItem(ACTIVE_SESSION_KEY);
        if (current) {
            const session = JSON.parse(current);
            localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
                ...session,
                accumulatedTime: sessionTime,
                isPaused: true
            }));
        }
    }, [sessionTime]);

    // Resume timer from pause
    const resumeTimer = useCallback(() => {
        setIsPaused(false);

        // Update active session
        const current = localStorage.getItem(ACTIVE_SESSION_KEY);
        if (current) {
            const session = JSON.parse(current);
            localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
                ...session,
                lastResumeTime: Date.now(),
                isPaused: false
            }));
        }
    }, []);

    // Stop timer and save session (when task is completed)
    const stopTimer = useCallback((saveSession = true) => {
        if (!activeTaskId || sessionTime === 0) {
            setActiveTaskId(null);
            setSessionTime(0);
            setIsPaused(false);
            localStorage.removeItem(ACTIVE_SESSION_KEY);
            return;
        }

        if (saveSession) {
            const existingData = workSessions[activeTaskId] || { totalSeconds: 0, sessions: [] };

            const updatedSessions = {
                ...workSessions,
                [activeTaskId]: {
                    totalSeconds: existingData.totalSeconds + sessionTime,
                    sessions: [
                        ...existingData.sessions,
                        {
                            start: new Date(Date.now() - sessionTime * 1000).toISOString(),
                            end: new Date().toISOString(),
                            duration: sessionTime
                        }
                    ]
                }
            };

            saveWorkSessions(updatedSessions);
        }

        localStorage.removeItem(ACTIVE_SESSION_KEY);
        setActiveTaskId(null);
        setSessionTime(0);
        setIsPaused(false);
    }, [activeTaskId, sessionTime, workSessions, saveWorkSessions]);

    // Toggle timer for a task (play/pause in same button)
    const toggleTimer = useCallback((taskId) => {
        if (activeTaskId === taskId) {
            if (isPaused) {
                resumeTimer();
            } else {
                pauseTimer();
            }
        } else {
            startTimer(taskId);
        }
    }, [activeTaskId, isPaused, startTimer, pauseTimer, resumeTimer]);

    // Format time
    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }
        return `${mins}:${String(secs).padStart(2, '0')}`;
    };

    // Format time in hours for dashboard
    const formatHours = (seconds) => {
        const hrs = (seconds / 3600).toFixed(1);
        return `${hrs}h`;
    };

    // Get total time for a task
    const getTaskTime = useCallback((taskId) => {
        const saved = workSessions[taskId]?.totalSeconds || 0;
        // Add current session if active
        if (activeTaskId === taskId) {
            return saved + sessionTime;
        }
        return saved;
    }, [workSessions, activeTaskId, sessionTime]);

    // Get total work time across all tasks
    const getTotalWorkTime = useCallback(() => {
        let total = Object.values(workSessions).reduce((acc, t) => acc + t.totalSeconds, 0);
        if (activeTaskId) {
            total += sessionTime;
        }
        return total;
    }, [workSessions, activeTaskId, sessionTime]);

    // Check if task timer is active
    const isTaskActive = useCallback((taskId) => {
        return activeTaskId === taskId;
    }, [activeTaskId]);

    // Check if task timer is paused
    const isTaskPaused = useCallback((taskId) => {
        return activeTaskId === taskId && isPaused;
    }, [activeTaskId, isPaused]);

    return {
        activeTaskId,
        isPaused,
        sessionTime,
        workSessions,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        toggleTimer,
        formatTime,
        formatHours,
        getTaskTime,
        getTotalWorkTime,
        isTaskActive,
        isTaskPaused
    };
};

export default useWorkTimer;
